import { useRequest } from "@certn/api";
import { Text } from "@certn/cascadia";
import { BasicButton, Card, SectionMessage } from "@certn/cascadia-react";
import { css } from "@emotion/css";
import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";

interface Props {
  reportId: string | undefined;
}

export function LegacyReportTab({ reportId }: Props) {
  const { t } = useTranslation();
  const request = useRequest();

  const [legacyCaseDetails, setLegacyCaseDetails] = useState<any>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<any>(null);

  // PDF document states
  const [pdfUrl, setPdfUrl] = useState<string | null>(null);
  const [isPdfLoading, setIsPdfLoading] = useState(false);
  const [pdfError, setPdfError] = useState<any>(null);

  const handleDownloadPDF = async () => {
    if (pdfUrl) {
      try {
        // Fetch the PDF as a blob to force download
        const response = await fetch(pdfUrl);
        const blob = await response.blob();

        // Create object URL for the blob
        const blobUrl = URL.createObjectURL(blob);

        // Create a temporary link element to trigger download
        const link = document.createElement("a");
        link.href = blobUrl;
        link.download = `report-${reportId}.pdf`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);

        // Clean up the blob URL
        URL.revokeObjectURL(blobUrl);
      } catch (error) {
        console.error("Failed to download PDF:", error);
        // Fallback to opening in new tab if download fails
        window.open(pdfUrl, "_blank");
      }
    }
  };

  useEffect(() => {
    if (!reportId) return;

    const fetchLegacyCase = async () => {
      try {
        setIsLoading(true);
        const response = await request(
          `/api/internal/client-portal/legacy-case/${reportId}/`
        );
        setLegacyCaseDetails(response);
        setError(null);
      } catch (error) {
        setError(error);
        setLegacyCaseDetails(null);
      } finally {
        setIsLoading(false);
      }
    };

    fetchLegacyCase();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reportId]);

  // Fetch PDF document URLs after case details are loaded
  useEffect(() => {
    if (!reportId || !legacyCaseDetails) return;

    const fetchPdfDocument = async () => {
      try {
        setIsPdfLoading(true);
        setPdfError(null);
        const response = (await request(
          `/api/internal/client-portal/legacy-case/${reportId}/documents/`
        )) as {
          pdf_report?: string | null;
          csv_report?: string | null;
          consent_documents?: string[];
        };

        if (response.pdf_report) {
          setPdfUrl(response.pdf_report);
        }
      } catch (error) {
        console.error("Failed to fetch PDF document:", error);
        setPdfError(error);
      } finally {
        setIsPdfLoading(false);
      }
    };

    fetchPdfDocument();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reportId, legacyCaseDetails]);

  if (error) {
    return (
      <SectionMessage
        type="danger"
        icon="error"
        sectionTitle={t("Error loading legacy report")}
      >
        {t(
          "Unable to load the legacy report for this case, please try again later."
        )}
      </SectionMessage>
    );
  }

  if (isLoading) {
    return (
      <Card
        className={styles.cardContainer}
        type="large"
        responsive
        testID="legacy-report-loading-card"
        border="border-base"
      >
        <Text style="global/heading-large">{t("Legacy Report")}</Text>
        <Text>{t("Loading...")}</Text>
      </Card>
    );
  }

  return (
    <div data-testid="legacy-report">
      <Card
        className={styles.cardContainer}
        type="large"
        responsive
        testID="legacy-report-card"
        border="border-base"
      >
        <div className={styles.reportHeader}>
          <Text style="global/heading-large">{t("Report")}</Text>
          <BasicButton
            leftIcon="download"
            onPress={handleDownloadPDF}
            isDisabled={!pdfUrl || isPdfLoading}
            testID="download-report-pdf-button"
          >
            {t("Download report PDF")}
          </BasicButton>
        </div>

        {legacyCaseDetails && (
          <div className={styles.reportContent}>
            <div className={styles.reportSection}>
              <Text style="global/heading-medium">{t("Profile details")}</Text>
              <div className={styles.infoGrid}>
                <div className={styles.infoItem}>
                  <Text style="global/heading-small">{t("First name")}</Text>
                  <Text>{legacyCaseDetails.applicant_first_name || "-"}</Text>
                </div>
                <div className={styles.infoItem}>
                  <Text style="global/heading-small">{t("Last name")}</Text>
                  <Text>{legacyCaseDetails.applicant_last_name || "-"}</Text>
                </div>
                <div className={styles.infoItem}>
                  <Text style="global/heading-small">{t("Email")}</Text>
                  <Text>{legacyCaseDetails.applicant_email || "-"}</Text>
                </div>
              </div>
            </div>

            <div className={styles.reportSection}>
              <Text style="global/heading-medium">{t("Case details")}</Text>
              <div className={styles.infoGrid}>
                <div className={styles.infoItem}>
                  <Text style="global/heading-small">{t("Report ID")}</Text>
                  <Text>{legacyCaseDetails.report_id}</Text>
                </div>
                <div className={styles.infoItem}>
                  <Text style="global/heading-small">{t("Date created")}</Text>
                  <Text>
                    {legacyCaseDetails.created
                      ? new Date(legacyCaseDetails.created).toLocaleDateString()
                      : "-"}
                  </Text>
                </div>
                <div className={styles.emptyItem}></div>
              </div>
            </div>

            {(legacyCaseDetails.listing_data?.unit ||
              legacyCaseDetails.listing_data?.address) && (
              <div className={styles.reportSection}>
                <Text style="global/heading-medium">
                  {t("Listing Information")}
                </Text>
                <div className={styles.infoGrid}>
                  {legacyCaseDetails.listing_data.unit && (
                    <div className={styles.infoItem}>
                      <Text style="global/heading-small">{t("Unit")}</Text>
                      <Text>{legacyCaseDetails.listing_data.unit}</Text>
                    </div>
                  )}
                  {legacyCaseDetails.listing_data.address && (
                    <div className={styles.infoItem}>
                      <Text style="global/heading-small">{t("Address")}</Text>
                      <Text>{legacyCaseDetails.listing_data.address}</Text>
                    </div>
                  )}
                  {/* Add empty items to ensure 3 columns */}
                  {(!legacyCaseDetails.listing_data.unit ||
                    !legacyCaseDetails.listing_data.address) && (
                    <div className={styles.emptyItem}></div>
                  )}
                  {!legacyCaseDetails.listing_data.unit &&
                    !legacyCaseDetails.listing_data.address && (
                      <div className={styles.emptyItem}></div>
                    )}
                </div>
              </div>
            )}

            {/* PDF Report Display */}
            {(pdfUrl || isPdfLoading || pdfError) && (
              <div className={styles.reportSection}>
                <Text style="global/heading-medium">
                  {t("Report Document")}
                </Text>
                {isPdfLoading && <Text>{t("Loading PDF report...")}</Text>}
                {pdfError && (
                  <SectionMessage
                    type="warning"
                    icon="warning"
                    sectionTitle={t("PDF Report Unavailable")}
                  >
                    {t("Unable to load the PDF report for this case.")}
                  </SectionMessage>
                )}
                {pdfUrl && !isPdfLoading && (
                  <>
                    <div className={styles.pdfContainer}>
                      <embed
                        src={`${pdfUrl}#view=FitH&toolbar=0&navpanes=0&scrollbar=0`}
                        type="application/pdf"
                        width="100%"
                        style={{ height: "90vh" }}
                        className={styles.pdfEmbed}
                      />
                    </div>
                    <Text style="global/body-secondary">
                      {t(
                        "Report results shall in no way be construed as Certn’s opinion, recommendation, or forecast on the solvency, creditworthiness, capability, intention, integrity or motives of the subject of the report. The report results merely reflect a recording of information received by Certn from various sources and does not constitute legal or other professional advice. Certn accepts no liability for any damages or losses arising from the opinions, comments, actions or decisions made by the recipient of the report results. The application of the report results requires independent judgment and skill."
                      )}
                    </Text>
                  </>
                )}
              </div>
            )}
          </div>
        )}
      </Card>
    </div>
  );
}

const styles = {
  cardContainer: css({
    padding: "var(--space-5) var(--space-4)",
    display: "flex",
    flexDirection: "column",
    gap: "var(--space-4)",
    marginBottom: 150, // Support floating button was hiding the disclaimer text
  }),
  reportHeader: css({
    display: "flex",
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    width: "100%",
    gap: "var(--space-4)",
  }),
  reportContent: css({
    display: "flex",
    flexDirection: "column",
    gap: "var(--space-6)",
  }),
  reportSection: css({
    display: "flex",
    flexDirection: "column",
    gap: "var(--space-3)",
  }),
  infoGrid: css({
    display: "grid",
    gridTemplateColumns: "repeat(3, 1fr)",
    gap: "var(--space-4)",
  }),
  infoItem: css({
    display: "flex",
    flexDirection: "column",
    gap: "var(--space-1)",
  }),
  emptyItem: css({
    // Empty item for padding grid rows to 3 columns
  }),
  checksList: css({
    display: "flex",
    flexDirection: "column",
    gap: "var(--space-2)",
  }),
  checkItem: css({
    padding: "var(--space-2) var(--space-3)",
    backgroundColor: "var(--surface-sunken)",
    borderRadius: "var(--border-radius-1)",
  }),
  pdfContainer: css({
    width: "100%",
    border: "1px solid var(--border-base)",
    borderRadius: "var(--border-radius-2)",
    overflow: "hidden",
    backgroundColor: "var(--surface-base)",
  }),
  pdfEmbed: css({
    display: "block",
    border: "none",
    outline: "none",
  }),
};
